<template>
  <section id="pricing" class="pt-6 md:pt-10 pb-10 md:pb-16 scroll-mt-20 relative">
    <!-- Background decorative elements -->
    <div class="absolute inset-0 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black_70%,transparent_100%)]">
      <div class="absolute top-10 left-10 w-64 h-64 bg-yellow-200/10 rounded-full blur-3xl"></div>
      <div class="absolute bottom-10 right-10 w-64 h-64 bg-yellow-100/20 rounded-full blur-3xl"></div>
    </div>
    
    <!-- Main content -->
    <div class="relative z-10">
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.7 }"
        :viewport="{ once: true, margin: '-100px' }"
  class="text-center mb-12 md:mb-14"
      >
        <h2 class="text-3xl md:text-4xl font-bold mb-4 text-slate-800 ">
          Simple, Transparent <span class="text-yellow-500">Pricing</span> 
        </h2>
        <p class="text-lg text-gray-700  max-w-2xl mx-auto">
          Choose the perfect package for your cruise group
        </p>
        <!-- Small decorative element -->
        <div class="flex justify-center mt-6">
          <div class="h-1 w-16 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full"></div>
        </div>
      </Motion>
      
      <!-- Loading state with improved animation -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16 gap-4">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="48"
        ></v-progress-circular>
        <p class="text-gray-600  animate-pulse">Loading packages...</p>
      </div>
      
      <!-- Pricing cards with improved layout and styling -->
      <div v-else class="px-4">
        <div class="max-w-7xl mx-auto grid md:grid-cols-3 gap-4 md:gap-8 lg:gap-12 equal-height-cards">
          <Motion
            v-for="(plan, index) in packages"
            :key="plan.id"
            :initial="{ opacity: 0, y: 40 }"
            :whileInView="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.6, delay: 0.15 * index }"
            :viewport="{ once: true, margin: '-50px' }"
            class="h-full"
          >
            <price-plan 
              :package-id="plan.id.toString()"
              :title="plan.name"
              :price="formatCurrency(plan.pricePerDuck, plan.currency || 'USD')"
              :subtitle="'per duck'"
              :features="getFeatures(plan.description)"
              :popular="index === 1" 
              :button-text="'Choose Plan'"
              :button-variant="index === 1 ? 'elevated' : 'outlined'"
              :bg-class="index === 1 ? 'bg-yellow-50/90 border-2 border-yellow-400/30 /30' : 'bg-white/90 '"
              @purchase-initiated="handlePurchaseInitiated"
              @login-required="handleLoginRequired(plan)"
              @show-cruise-form="showCruiseDialog"
            />
          </Motion>
        </div>
        
        <!-- Additional information section -->
        <div class="mt-16 max-w-3xl mx-auto text-center">
          <Motion
            :initial="{ opacity: 0 }"
            :whileInView="{ opacity: 1 }"
            :transition="{ duration: 0.5 }"
            :viewport="{ once: true }"
          >
            <div class="bg-blue-50/80 backdrop-blur-sm rounded-xl p-6 border border-blue-100 /30">
              <div class="flex items-center justify-center mb-4">
                <div class="bg-blue-100/80 rounded-full p-2">
                  <v-icon size="24" color="blue">mdi-information</v-icon>
                </div>
              </div>
              <h3 class="text-lg font-semibold mb-2 text-slate-800 ">Need a custom package?</h3>
              <p class="text-gray-700 ">Contact us for volume discounts or special requests for your cruise group.</p>
              <router-link to="/contact" class="mt-4 inline-block text-blue-500 hover:text-blue-600">
                Get in touch <v-icon size="small">mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </Motion>
        </div>
      </div>
    </div>
    
    <!-- Login Dialog for unauthenticated users -->
    <LoginDialog
      v-model="showLoginDialog"
      :redirect-url="'/'"
      :show-message="'Please login to purchase a package'"
      @notification="handleNotification"
      @login-success="handleLoginSuccess"
    />
    
    <!-- Cruise Details Dialog -->
    <CruiseFormDialog
      v-model="showCruiseFormDialog"
      :package-id="selectedPackage.id"
      :package-name="selectedPackage.name"
      :package-price="selectedPackage.price"
      :package-currency="selectedPackage.currency"
      :package-data="{ allowsAdditionalDucks: selectedPackage.allowsAdditionalDucks }"
      @checkout-success="handleCheckoutSuccess"
      @session-expired="handleSessionExpired"
    />
    
    <!-- Notifications -->
    <v-snackbar
      v-model="showNotification"
      :color="notificationColor"
      :timeout="3000"
      location="top"
    >
      {{ notificationMessage }}
    </v-snackbar>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import PricePlan from '@/components/home/PricePlan.vue';
import { Motion } from 'motion-v';
import LoginDialog from '@/components/auth/LoginDialog.vue';
import CruiseFormDialog from '@/components/checkout/CruiseFormDialog.vue';
import { usePackagesStore } from '@/stores/packagesStore';
import { useAuthStore } from '@/stores/authStore';

// Package interface based on API schema
interface Package {
  id: number;
  name: string;
  description: string;
  maxDucks: number;
  price: number;
  pricePerDuck: number;
  currency: string;
  allowsAdditionalDucks: boolean;
}

// Component state
const showLoginDialog = ref(false);
const packagesStore = usePackagesStore();
const loading = computed(() => packagesStore.loading);
const packages = computed(() => packagesStore.sortedPackages);
const authStore = useAuthStore();

// State for cruise form dialog
const showCruiseFormDialog = ref(false);
const selectedPackage = ref<{
  id: string;
  name: string;
  price: string;
  currency: string;
  allowsAdditionalDucks?: boolean;
}>({
  id: '',
  name: '',
  price: '',
  currency: 'USD'
});

// Add state to remember package selected before login
const pendingPackageSelection = ref<Package | null>(null);

// State for notifications
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationColor = ref('info');

onMounted(async () => {
  // Use the packages from the store, which might already be loaded
  // Only fetch if not already in progress or cached
  if (!packagesStore.loading && packages.value.length === 0) {
    await packagesStore.fetchPackages();
  }
});

// Helper functions
const formatCurrency = (price: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(price);
};

const getFeatures = (description: string) => {
  // Generate features from description or return default ones
  if (!description) return ['Duck tracking', 'App access', 'Email notifications'];
  
  // Split description by commas, periods, or semicolons
  return description.split(/[,.;]+/).filter(feature => feature.trim().length > 0);
};

const handlePurchaseInitiated = (packageId: string) => {
  console.log(`Purchase initiated for package: ${packageId}`);
  // Any additional tracking or analytics can be added here
};

// Handle login required - remember the package selection
const handleLoginRequired = (plan: Package) => {
  pendingPackageSelection.value = plan;
  showLoginDialog.value = true;
};

// Handle successful login
const handleLoginSuccess = () => {
  // If we have a pending package selection, show the cruise form dialog
  if (pendingPackageSelection.value) {
    showCruiseDialog({
      packageId: pendingPackageSelection.value.id.toString(),
      packageTitle: pendingPackageSelection.value.name,
      packagePrice: formatCurrency(pendingPackageSelection.value.pricePerDuck, 
                                   pendingPackageSelection.value.currency || 'USD')
    });
    pendingPackageSelection.value = null;
  }
};

// Handle session expired
const handleSessionExpired = () => {
  // Log the user out
  authStore.logout();
  
  // Show notification
  notificationMessage.value = 'Your session has expired. Please log in again.';
  notificationColor.value = 'error';
  showNotification.value = true;
  
  // Show login dialog
  setTimeout(() => {
    showLoginDialog.value = true;
  }, 500);
};

// Handle notifications
const handleNotification = ({ message, color }: { message: string; color?: string }) => {
  notificationMessage.value = message;
  notificationColor.value = color || 'info';
  showNotification.value = true;
};

const showCruiseDialog = (packageInfo: {packageId: string, packageTitle: string, packagePrice: string}) => {
  // Find the full package data from packages store
  const fullPackage = packages.value.find(pkg => pkg.id === parseInt(packageInfo.packageId));
  
  selectedPackage.value = {
    id: packageInfo.packageId,
    name: packageInfo.packageTitle,
    price: packageInfo.packagePrice,
    currency: fullPackage?.currency || 'USD',
    allowsAdditionalDucks: fullPackage?.allowsAdditionalDucks
  };
  showCruiseFormDialog.value = true;
};

const handleCheckoutSuccess = (checkoutUrl: string) => {
  if (checkoutUrl) {
    window.location.href = checkoutUrl;
  }
};
</script>

<style scoped>
.equal-height-cards {
  height: 100%;
  display: grid;
  align-items: stretch;
}

.equal-height-cards > * {
  height: 100%;
}

/* Additional styles for the enhanced pricing section */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Improved media queries for better mobile experience */
@media (max-width: 768px) {
  .px-4 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>